import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, ChevronLeft, ChevronRight, CheckCircle2, Coffee, Heart, Loader2,
} from 'lucide-react';
import { useAuth, API } from '../App';
import { Button } from '@/components/ui/button';

const STATUS_META = {
  working:    { label: 'Working',    color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2 },
  day_off:    { label: 'Day Off',    color: 'bg-slate-200 text-slate-800 border-slate-400',       icon: Coffee },
  sick_leave: { label: 'Sick Leave', color: 'bg-amber-100 text-amber-800 border-amber-300',       icon: Heart },
  unknown:    { label: '',           color: 'bg-white text-slate-300 border-slate-200',            icon: null },
};

// "holiday" status is hidden from drivers (relabeled as Working).
// "unknown" status (no record) is shown as a neutral empty cell.
const DRIVER_STATUS = (s) => {
  if (s === 'holiday') return 'working';
  if (!s || s === 'unknown') return 'unknown';
  return s;
};

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export default function DriverAttendanceCalendar() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/driver-attendance/my/calendar`, {
        params: { year, month },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [token, year, month]);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  const goPrev = () => {
    if (month === 1) { setMonth(12); setYear((y) => y - 1); } else { setMonth((m) => m - 1); }
  };
  const goNext = () => {
    if (month === 12) { setMonth(1); setYear((y) => y + 1); } else { setMonth((m) => m + 1); }
  };

  const grid = useMemo(() => {
    if (!data?.days) return [];
    const first = new Date(year, month - 1, 1);
    const firstWeekday = (first.getDay() + 6) % 7; // Monday-start
    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    data.days.forEach((d) => cells.push(d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [data, year, month]);

  const rawTotals = data?.totals || {};
  // Merge any holiday days into the Working count for drivers (holiday hidden from drivers).
  const totals = {
    working: (rawTotals.working || 0) + (rawTotals.holiday || 0),
    day_off: rawTotals.day_off || 0,
    sick_leave: rawTotals.sick_leave || 0,
  };
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/driver')} data-testid="back-to-dashboard">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h1 className="text-lg font-semibold text-slate-900">My Attendance</h1>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Month navigation */}
        <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={goPrev} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="text-base font-semibold text-slate-800" data-testid="cal-title">
            {MONTHS[month - 1]} {year}
          </div>
          <Button variant="ghost" size="sm" onClick={goNext} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-3 gap-3" data-testid="cal-totals">
          {Object.entries(STATUS_META).filter(([k]) => k !== 'unknown').map(([k, meta]) => {
            const Icon = meta.icon;
            return (
              <div key={k} className={`border rounded-md px-3 py-2.5 ${meta.color}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs font-medium">{meta.label}</div>
                  {Icon && <Icon className="h-4 w-4 opacity-80" />}
                </div>
                <div className="text-2xl font-bold mt-1">{totals[k] || 0}</div>
              </div>
            );
          })}
        </div>

        {/* Calendar grid */}
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="grid grid-cols-7 bg-slate-100 text-xs font-semibold text-slate-600">
            {WEEKDAYS.map((w) => (<div key={w} className="px-2 py-2 text-center">{w}</div>))}
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <div className="grid grid-cols-7">
              {grid.map((cell, idx) => {
                if (!cell) return <div key={idx} className="border-t border-slate-100 min-h-[64px] bg-slate-50/40" />;
                const day = parseInt(cell.date.split('-')[2], 10);
                const status = DRIVER_STATUS(cell.status);
                const meta = STATUS_META[status] || STATUS_META.working;
                const isToday = cell.date === todayIso;
                return (
                  <div
                    key={cell.date}
                    className={`border-t border-l border-slate-100 min-h-[64px] p-1.5 ${isToday ? 'bg-orange-100 ring-2 ring-orange-400 ring-inset' : ''}`}
                    data-testid={`cal-day-${cell.date}`}
                  >
                    <div className={`text-xs font-semibold ${isToday ? 'text-orange-700' : 'text-slate-700'}`}>{day}</div>
                    {status !== 'unknown' && (
                      <div className={`mt-1 text-[10px] inline-flex px-1.5 py-0.5 rounded border ${meta.color}`}>
                        {meta.label}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="text-xs text-slate-500 text-center">
          Read-only view. Contact supervisor to change any status.
        </div>
      </div>
    </div>
  );
}
